import { usePictureInPictureModeContext } from '@/features/approving_invoice_reports/components/List/context/PictureInPictureModeProvider';
import {
  TAB_KEYS,
  TabKey,
  usePaymentDetail,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import { useCallback, useEffect } from 'react';

export type usePiPActionsProps = {
  tabs: Array<{
    key: TabKey;
    children: string;
    isDisabled?: boolean;
  }>;
};

export const usePiPActions = ({
  tabs,
}: usePiPActionsProps): {
  handleOpenPictureInPicture: () => void;
  handleUpdatePictureInPictureContent: () => void;
  pdfViewerContainerRef: React.RefObject<HTMLDivElement>;
} => {
  const {
    isPIPMode,
    setIsPIPMode,
    updatePictureInPictureContent,
    openPictureInPicture,
    pdfViewerContainerRef,
    invoiceFileId,
  } = usePictureInPictureModeContext();

  const {
    isRightPaneCollapsed,
    setIsRightPaneCollapsed,
    activeTab,
    setActiveTab,
  } = usePaymentDetail();

  const onClose = useCallback(() => {
    setIsPIPMode(false);
  }, [setIsPIPMode]);

  const handleOpenPictureInPicture = useCallback(() => {
    openPictureInPicture(true, onClose);
    setIsPIPMode(true);
  }, [openPictureInPicture, setIsPIPMode, onClose]);

  const handlePIPModeOpenActions = useCallback(() => {
    if (isPIPMode && activeTab === TAB_KEYS.ATTACHED_INVOICE) {
      if (tabs.length > 1) {
        setActiveTab(TAB_KEYS.TRANSACTION_DOCUMENTS);
      } else {
        setIsRightPaneCollapsed(true);
      }
    }
  }, [isPIPMode, activeTab, setActiveTab, tabs, setIsRightPaneCollapsed]);

  const handlePIPModeCloseActions = useCallback(() => {
    if (!isPIPMode && isRightPaneCollapsed) {
      setIsRightPaneCollapsed(false);
    }
    setIsPIPMode(null);
  }, [isPIPMode, setIsRightPaneCollapsed, setIsPIPMode, isRightPaneCollapsed]);

  useEffect(() => {
    if (isPIPMode === null) return;
    if (isPIPMode) {
      handlePIPModeOpenActions();
    } else {
      handlePIPModeCloseActions();
    }
  }, [isPIPMode, handlePIPModeOpenActions, handlePIPModeCloseActions]);

  /** When invoice file id null, it means this report does not have invoice file, so we need to update the picture in picture content */
  useEffect(() => {
    if (invoiceFileId === null && isPIPMode) {
      openPictureInPicture(false);
      handlePIPModeCloseActions();
      setActiveTab(TAB_KEYS.ATTACHED_INVOICE);
    }
  }, [
    invoiceFileId,
    openPictureInPicture,
    isPIPMode,
    handlePIPModeCloseActions,
    setActiveTab,
  ]);

  const handleUpdatePictureInPictureContent = useCallback(() => {
    updatePictureInPictureContent();
  }, [updatePictureInPictureContent]);
  return {
    handleOpenPictureInPicture,
    handleUpdatePictureInPictureContent,
    pdfViewerContainerRef,
  };
};
